import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { UserCheck, Baby, User, AlertTriangle, Shield } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AcceptInvitation = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const { login } = useAuth();
  
  const [inviteInfo, setInviteInfo] = useState(null);
  const [loadingInfo, setLoadingInfo] = useState(true);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  
  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: '',
    first_name: '',
    last_name: '',
    agrement_number: ''
  });
  
  // Fetch invitation info on mount
  useEffect(() => {
    const fetchInfo = async () => {
      try {
        const res = await axios.get(`${API}/invitations/info/${token}`);
        setInviteInfo(res.data);
        if (res.data.nanny_name) {
          // Split full name into first_name and last_name
          const parts = res.data.nanny_name.trim().split(/\s+/);
          const firstName = parts[0] || '';
          const lastName = parts.slice(1).join(' ') || '';
          setFormData(prev => ({ 
            ...prev, 
            first_name: firstName,
            last_name: lastName
          }));
        }
      } catch (err) {
        const detail = err.response?.data?.detail || err.response?.data?.error_description || 'Invitation invalide ou expirée';
        setError(detail);
      } finally {
        setLoadingInfo(false);
      }
    };
    fetchInfo();
  }, [token]);
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.password !== formData.confirmPassword) {
      toast.error('Les mots de passe ne correspondent pas');
      return;
    }
    
    setSubmitting(true);
    try {
      // withCredentials: true → backend sets HttpOnly refresh cookie
      const res = await axios.post(`${API}/invitations/accept/email`, {
        token,
        password: formData.password,
        first_name: formData.first_name,
        last_name: formData.last_name,
        agrement_number: formData.agrement_number || undefined
      }, { withCredentials: true });
      
      toast.success(res.data.message || 'Invitation acceptée ! Bienvenue sur Nannyou.');
      
      // Redirect to login — the refresh cookie is already set by the backend.
      // A full page reload allows AuthContext to restore the session from the cookie.
      setTimeout(() => {
        window.location.href = '/';
      }, 500);
    } catch (err) {
      const detail = err.response?.data?.detail || 'Erreur lors de l\'acceptation';
      toast.error(detail);
    } finally {
      setSubmitting(false);
    }
  };
  
  if (loadingInfo) {
    return (
      <div 
        className="min-h-screen flex items-center justify-center py-8"
        style={{ background: 'linear-gradient(135deg, var(--bg-tertiary) 0%, var(--bg-secondary) 100%)' }}
      >
        <Card className="w-full max-w-md mx-4">
          <CardHeader className="text-center">
            <Skeleton className="h-16 w-16 rounded-full mx-auto mb-4" />
            <Skeleton className="h-8 w-48 mx-auto" />
            <Skeleton className="h-4 w-64 mx-auto mt-2" />
          </CardHeader>
          <CardContent className="space-y-4">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
          </CardContent>
        </Card>
      </div>
    );
  }
  
  if (error) {
    return (
      <div 
        className="min-h-screen flex items-center justify-center py-8"
        style={{ background: 'linear-gradient(135deg, var(--bg-tertiary) 0%, var(--bg-secondary) 100%)' }}
        data-testid="accept-invite-error"
      >
        <Card className="w-full max-w-md mx-4">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 w-16 h-16 rounded-full flex items-center justify-center bg-[var(--error-light)]">
              <AlertTriangle className="h-8 w-8 text-[var(--error)]" />
            </div>
            <CardTitle className="text-2xl" style={{ fontFamily: 'var(--font-secondary)' }}>
              Invitation invalide
            </CardTitle>
            <CardDescription>{error}</CardDescription>
          </CardHeader>
          <CardContent className="text-center">
            <p className="text-sm text-[var(--text-secondary)] mb-4">
              Demandez à votre employeur de vous renvoyer une nouvelle invitation.
            </p>
            <Button onClick={() => navigate('/login')} variant="outline" data-testid="back-to-login-btn">
              Retour à la connexion
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }
  
  return (
    <div 
      className="min-h-screen flex items-center justify-center py-8"
      style={{ background: 'linear-gradient(135deg, var(--bg-tertiary) 0%, var(--bg-secondary) 100%)' }}
      data-testid="accept-invite-page"
    >
      <Card className="w-full max-w-md mx-4">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 w-16 h-16 rounded-full flex items-center justify-center" style={{ backgroundColor: 'var(--primary-400)' }}>
            <UserCheck className="h-8 w-8 text-white" />
          </div>
          <CardTitle className="text-2xl" style={{ fontFamily: 'var(--font-secondary)' }}>
            Rejoindre Nannyou
          </CardTitle>
          <CardDescription>
            Vous avez été invité(e) par un parent employeur
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          {/* Invitation details */}
          <div className="p-4 rounded-lg bg-[var(--primary-50)] border border-[var(--primary-200)] space-y-2">
            <div className="flex items-center gap-2 text-sm">
              <User className="h-4 w-4 text-[var(--primary-400)]" />
              <span className="text-[var(--text-secondary)]">Parent :</span>
              <strong style={{ color: 'var(--primary-700)' }}>{inviteInfo.parent_name}</strong>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <Baby className="h-4 w-4 text-[var(--primary-400)]" />
              <span className="text-[var(--text-secondary)]">Enfant :</span>
              <strong style={{ color: 'var(--primary-700)' }}>
                {inviteInfo.child_first_name || inviteInfo.child_name}
              </strong>
            </div>
            {inviteInfo.nanny_email && (
              <div className="flex items-center gap-2 text-sm">
                <Shield className="h-4 w-4 text-[var(--primary-400)]" />
                <span className="text-[var(--text-secondary)]">Email :</span>
                <strong style={{ color: 'var(--primary-700)' }}>{inviteInfo.nanny_email}</strong>
              </div>
            )}
          </div>
          
          {/* Registration form — only if account doesn't exist */}
          {!inviteInfo.account_exists ? (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="first_name">Prénom *</Label>
                <Input
                  id="first_name"
                  type="text"
                  placeholder="Marie"
                  value={formData.first_name}
                  onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                  required
                  data-testid="invite-firstname-input"
                />
              </div>
              <div>
                <Label htmlFor="last_name">Nom *</Label>
                <Input
                  id="last_name"
                  type="text"
                  placeholder="Dupont"
                  value={formData.last_name}
                  onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                  required
                  data-testid="invite-lastname-input"
                />
              </div>
            </div>
            
            <div>
              <Label htmlFor="password">Créez votre mot de passe *</Label>
              <Input
                id="password"
                type="password"
                placeholder="••••••••"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                required
                minLength={8}
                data-testid="invite-password-input"
              />
              <p className="text-xs text-[var(--text-tertiary)] mt-1">Min. 8 caractères, 1 majuscule, 1 chiffre</p>
            </div>
            
            <div>
              <Label htmlFor="confirmPassword">Confirmer le mot de passe *</Label>
              <Input
                id="confirmPassword"
                type="password"
                placeholder="••••••••"
                value={formData.confirmPassword}
                onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                required
                data-testid="invite-confirm-password-input"
              />
              {formData.password && formData.confirmPassword && formData.password !== formData.confirmPassword && (
                <p className="text-xs text-[var(--error)] mt-1">Les mots de passe ne correspondent pas</p>
              )}
            </div>
            
            {/* Optional professional fields */}
            <details className="group">
              <summary className="cursor-pointer text-sm font-medium flex items-center gap-1 py-1" style={{ color: 'var(--primary-500)' }}>
                Identifiants professionnels (optionnel)
              </summary>
              <div className="mt-3 space-y-3">
                <div>
                  <Label htmlFor="agrement_number">N° d'agrément</Label>
                  <Input
                    id="agrement_number"
                    type="text"
                    placeholder="Ex: AG-75-1234"
                    value={formData.agrement_number}
                    onChange={(e) => setFormData({ ...formData, agrement_number: e.target.value })}
                    data-testid="invite-agrement-input"
                  />
                </div>
              </div>
            </details>
            
            <Button
              type="submit"
              className="w-full h-12 text-base font-semibold"
              disabled={submitting || (formData.password !== formData.confirmPassword) || !formData.first_name || !formData.last_name || !formData.password}
              style={{ backgroundColor: 'var(--primary-400)' }}
              data-testid="accept-invite-button"
            >
              {submitting ? 'Acceptation en cours...' : 'Accepter l\'invitation et créer mon compte'}
            </Button>
          </form>
          ) : (
          <div className="space-y-4">
            <div className="p-4 rounded-lg bg-[var(--success-light)] border border-[var(--success)]">
              <p className="text-sm text-[var(--success-dark)]">
                Un compte existe déjà pour <strong>{inviteInfo.nanny_email}</strong>. Connectez-vous pour accepter l'invitation automatiquement.
              </p>
            </div>
            <Button
              className="w-full h-12 text-base font-semibold"
              style={{ backgroundColor: 'var(--primary-400)' }}
              onClick={() => navigate(`/login?invite=${token}`)}
              data-testid="accept-invite-login-button"
            >
              Se connecter pour accepter
            </Button>
          </div>
          )}
          
          <div className="text-center text-sm text-[var(--text-secondary)]">
            Déjà un compte ?{' '}
            <a href="/login" className="text-[var(--primary-400)] hover:underline" data-testid="login-link-invite">
              Se connecter
            </a>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AcceptInvitation;
